import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChefHat, Star } from 'lucide-react';
import { useAuth } from './../components/AuthContext';
import { useUserId } from './../components/UserIdHook';
import LoadingImage from '../assets/loading.gif';
import { useRecipeService, Recipe } from '../services/recipeService';
import './RecipeList.scss';

// Constants
const RECIPES_PER_PAGE = 8;
const MEAL_TYPES = ['Breakfast', 'Lunch', 'Dinner', 'Snack', 'Dessert'];
const DIET_TYPES = ['Vegetarian', 'Vegan', 'Gluten-free', 'Keto', 'Paleo', 'Low-carb'];
const CUISINES = ['Italian', 'Chinese', 'Indian', 'Mexican', 'French', 'Japanese', 'Thai', 'Greek', 'Spanish', 'American', 'Korean', 'Vietnamese'];

// Types
interface FilterState {
    meal_type: string;
    diet_type: string;
    cuisine: string;
}

// Helper Components
const CollapsibleSection: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => {
    const [isOpen, setIsOpen] = useState(true);
    return (
        <div className="collapsible-section">
            <div className={`section-header ${isOpen ? 'open' : ''}`} onClick={() => setIsOpen(!isOpen)}>
                <h3>{title}</h3>
                <ChevronDown className="toggle-icon" />
            </div>
            <div className={`section-content ${isOpen ? 'open' : ''}`}>{children}</div>
        </div>
    );
};

const RecipeCard: React.FC<{ recipe: Recipe; onClick: () => void; isOwnRecipe: boolean }> = ({ recipe, onClick, isOwnRecipe }) => (
    <div className={`recipe-card ${isOwnRecipe ? 'own-recipe' : ''}`} onClick={onClick}>
        <h3>{recipe.recipe_name}</h3>
        <p>{recipe.description}</p>
        {isOwnRecipe && <Star className="own-recipe-star" size={20} />}
    </div>
);

const Pagination: React.FC<{ currentPage: number; totalPages: number; onPageChange: (page: number) => void }> = 
    ({ currentPage, totalPages, onPageChange }) => (
    <div className="pagination">
        {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
            <button
                key={page}
                onClick={() => onPageChange(page)}
                className={page === currentPage ? 'active' : ''}
            >
                {page}
            </button>
        ))}
    </div>
);

// Main Component
const RecipeList: React.FC = () => {
    const [recipes, setRecipes] = useState<Recipe[]>([]);
    const [filteredRecipes, setFilteredRecipes] = useState<Recipe[]>([]);
    const [inputText, setInputText] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [isSidebarVisible, setIsSidebarVisible] = useState(false);
    const [showMyCulinaryCreations, setShowMyCulinaryCreations] = useState(false);
    const [activeFilters, setActiveFilters] = useState<FilterState>({
        meal_type: 'all',
        diet_type: 'all',
        cuisine: 'all'
    });

    const navigate = useNavigate();
    const { user } = useAuth();
    const user_id = useUserId(user) ?? '';
    const { fetchRecipes } = useRecipeService();

    // Load recipes
    useEffect(() => {
        const loadRecipes = async () => {
            setIsLoading(true);
            try {
                const { recipes: fetchedRecipes } = await fetchRecipes();
                setRecipes(fetchedRecipes);
            } catch (error) {
                console.error('Error loading recipes:', error);
                setError('Failed to load recipes. Please try again.');
            } finally {
                setIsLoading(false);
            }
        };
        loadRecipes();
    }, [fetchRecipes]);

    // Apply filters
    useEffect(() => {
        console.log('Applying filters:', { inputText, activeFilters, showMyCulinaryCreations });
        const filtered = recipes.filter((recipe) => {
            const matchesSearch = 
                recipe.recipe_name.toLowerCase().includes(inputText.toLowerCase()) ||
                recipe.description.toLowerCase().includes(inputText.toLowerCase());
            const matchesMealType = activeFilters.meal_type === 'all' || recipe.meal_type === activeFilters.meal_type;
            const matchesDietType = activeFilters.diet_type === 'all' || recipe.diet_type === activeFilters.diet_type;
            const matchesCuisine = activeFilters.cuisine === 'all' || recipe.cuisine === activeFilters.cuisine;
            const matchesCreator = !showMyCulinaryCreations || recipe.createdBy === user_id;

            return matchesSearch && matchesMealType && matchesDietType && matchesCuisine && matchesCreator;
        });
        console.log('Filtered recipes:', filtered.length);
        setFilteredRecipes(filtered);
        setCurrentPage(1);
    }, [recipes, inputText, activeFilters, showMyCulinaryCreations, user_id]);

    const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        console.log('Search text changed:', event.target.value);
        setInputText(event.target.value);
    };

    const handleFilterByCategory = (category: keyof FilterState, value: string) => {
        console.log('Filter changed:', category, value);
        setActiveFilters(prev => ({
            ...prev,
            [category]: value
        }));
    };

    const handleToggleMyCulinaryCreations = () => {
        console.log('Toggling My Culinary Creations');
        setShowMyCulinaryCreations(prev => !prev);
    };

    
    const handleRecipeClick = (recipeId: string) => navigate(`/recipe/${recipeId}`);
    const toggleSidebar = () => setIsSidebarVisible(prev => !prev);

    const currentRecipes = useMemo(() => {
        const startIndex = (currentPage - 1) * RECIPES_PER_PAGE;
        return filteredRecipes.slice(startIndex, startIndex + RECIPES_PER_PAGE);
    }, [filteredRecipes, currentPage]);

    const totalPages = Math.ceil(filteredRecipes.length / RECIPES_PER_PAGE);

    return (
        <div className="recipe-list-page">
            <div className="recipe-list-container">
                <aside className={`sidebar ${isSidebarVisible ? 'active' : ''}`}>
                    <div className="search-container">
                        <input
                            type="text"
                            placeholder="Search recipes..."
                            value={inputText}
                            onChange={handleSearch}
                        />
                    </div>
                    <div className="my-culinary-creations">
                        <button 
                            className={`culinary-creations-button ${showMyCulinaryCreations ? 'active' : ''}`}
                            onClick={handleToggleMyCulinaryCreations}
                        >
                            <ChefHat className="chef-hat-icon" />
                            My Culinary Creations
                        </button>
                    </div>
                    {['Meal Type', 'Diet Type', 'Cuisine'].map((category) => (
                        <CollapsibleSection key={category} title={category}>
                            <div className="options-container">
                                <button 
                                    className={`option-button ${activeFilters[category.toLowerCase().replace(' ', '_') as keyof FilterState] === 'all' ? 'active' : ''}`}
                                    onClick={() => handleFilterByCategory(category.toLowerCase().replace(' ', '_') as keyof FilterState, 'all')}
                                >
                                    All
                                </button>
                                {(category === 'Meal Type' ? MEAL_TYPES : 
                                  category === 'Diet Type' ? DIET_TYPES : 
                                  CUISINES).map((type) => (
                                    <button
                                        key={type}
                                        className={`option-button ${activeFilters[category.toLowerCase().replace(' ', '_') as keyof FilterState] === type ? 'active' : ''}`}
                                        onClick={() => handleFilterByCategory(category.toLowerCase().replace(' ', '_') as keyof FilterState, type)}
                                    >
                                        {type}
                                    </button>
                                ))}
                            </div>
                        </CollapsibleSection>
                    ))}
                </aside>
                <main className="content-area">
                    <div className="recipe-list">
                        <button className="mobile-menu-button" onClick={toggleSidebar}>☰</button>
                        {currentRecipes.length > 0 ? (
                            currentRecipes.map((recipe) => (
                                <RecipeCard
                                    key={recipe.recipe_id}
                                    recipe={recipe}
                                    onClick={() => handleRecipeClick(recipe.recipe_id)}
                                    isOwnRecipe={recipe.createdBy === user_id}
                                />
                            ))
                        ) : (
                            <div className="no-recipes-found">
                                <svg className="icon" viewBox="0 0 24 24" width="100" height="100">
                                    <path fill="currentColor" d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
                                </svg>
                                <h2>Oops! No recipes found</h2>
                                <p>It seems our kitchen is empty for this search.</p>
                                <p>Try adjusting your filters or search for a different dish!</p>
                            </div>
                        )}
                    </div>
                    {filteredRecipes.length > RECIPES_PER_PAGE && (
                        <Pagination
                            currentPage={currentPage}
                            totalPages={totalPages}
                            onPageChange={setCurrentPage}
                        />
                    )}
                </main>
            </div>
        </div>
    );
};

export default RecipeList;